import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import {
  WhatsappTemplateCategory,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { WhatsappDisplayNameStatus } from "@jugl-web/rest-api/integrations/models/common-types/WhatsappDisplayNameStatus";
import { Button, MultiSectionLayout, TabsLayout } from "@jugl-web/ui-kit";
import {
  SideMenuList,
  SideMenuListItem,
  SideMenuPanel,
} from "@jugl-web/ui-kit/layout/SideMenuLayout";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useWhatsappAccount } from "../../../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as BusinessIcon } from "./assets/business.svg";
import { ReactComponent as ContactIcon } from "./assets/contact.svg";
import { ReactComponent as ExclamationMarkIcon } from "./assets/exclamation-mark.svg";
import { ReactComponent as HeartIcon } from "./assets/heart.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as ProfileIcon } from "./assets/profile.svg";
import { ReactComponent as ShieldIcon } from "./assets/shield.svg";
import { WhatsappAuthenticationTab } from "./components/WhatsappAuthenticationTab";
import { WhatsappBusinessVerificationTab } from "./components/WhatsappBusinessVerificationTab";
import { WhatsappClientsContactDetailsTab } from "./components/WhatsappClientsContactDetailsTab";
import { WhatsappGeneralInfoTab } from "./components/WhatsappGeneralInfoTab";
import { WhatsappLogsHistoryTab } from "./components/WhatsappLogsHistoryTab";
import { WhatsappProfileTab } from "./components/WhatsappProfileTab";
import { WhatsappQualityRatingTab } from "./components/WhatsappQualityRatingTab";
import { WhatsappTemplatesTab } from "./components/WhatsappTemplatesTab";
import { WhatsappAccountMenuTab, WhatsappAccountTab } from "./types";
import { shouldShowBusinessVerificationAlert } from "./utils/shouldShowBusinessVerificationAlert";

export const WhatsappAccountScreen: React.FC<{ tab?: WhatsappAccountTab }> = ({
  tab,
}) => {
  const { t } = useTranslations();
  const { whatsappAgent, isConnected } = useWhatsappAccount();
  const { subscriptionRequiredAction } = useEntityProvider();
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();
  const { requestNavigation } = useNavigationGuardContext();
  const { isCx } = useAppVariant();

  const [selectedTab, setSelectedTab] = useState<WhatsappAccountTab>(
    WhatsappAccountTab.account
  );

  useEffect(() => {
    if (tab) {
      setSelectedTab(tab);
    }
  }, [tab]);

  const [selectedMenuTab, setSelectedMenuTab] =
    useState<WhatsappAccountMenuTab>(WhatsappAccountMenuTab.generalInfo);

  const shouldShowSideMenu =
    selectedTab === WhatsappAccountTab.account &&
    whatsappAgent?.phone_number.status === "CONNECTED";

  const { data: whatsappBusinessStatus } =
    integrationsApi.useGetWhatsappBusinessVerificationStatusQuery(
      shouldShowSideMenu
        ? {
            entityId,
          }
        : skipToken
    );

  const { data: whatsappQualityRating } =
    integrationsApi.useGetWhatsappQualityRatingQuery(
      shouldShowSideMenu
        ? {
            entityId,
          }
        : skipToken
    );

  const handleSafeTabChange = (newTab: WhatsappAccountTab) => {
    requestNavigation(() => setSelectedTab(newTab));
  };

  const handleSafeMenuTabChange = (newMenuTab: WhatsappAccountMenuTab) => {
    requestNavigation(() => setSelectedMenuTab(newMenuTab));
  };

  const sideMenuItems = useMemo(
    () => [
      {
        id: WhatsappAccountMenuTab.generalInfo,
        label: t({
          id: "client-support-page.general-info",
          defaultMessage: "General Info",
        }),
        startIcon: <InfoIcon />,
      },
      {
        id: WhatsappAccountMenuTab.businessVerification,
        label: t({
          id: "client-support-page.business-verification",
          defaultMessage: "Business Verification",
        }),
        startIcon: <BusinessIcon />,
        endIcon:
          whatsappBusinessStatus &&
          shouldShowBusinessVerificationAlert(whatsappBusinessStatus.status) ? (
            <ExclamationMarkIcon />
          ) : undefined,
      },
      {
        id: WhatsappAccountMenuTab.twoFactorAuthentication,
        label: t({
          id: "client-support-page.two-factor-authentication",
          defaultMessage: "Two-factor authentication",
        }),
        startIcon: <ShieldIcon />,
      },
      {
        id: WhatsappAccountMenuTab.waProfile,
        label: t({
          id: "client-support-page.wa-profile",
          defaultMessage: "WA Profile",
        }),
        startIcon: <ProfileIcon />,
        endIcon:
          whatsappAgent?.phone_number.name_status ===
          WhatsappDisplayNameStatus.DECLINED ? (
            <ExclamationMarkIcon />
          ) : undefined,
      },
      {
        id: WhatsappAccountMenuTab.clientsContactDetails,
        label: t({
          id: "client-support-page.clients-contact-details",
          defaultMessage: "Clients contact details",
        }),
        startIcon: <ContactIcon />,
      },
      {
        id: WhatsappAccountMenuTab.qualityRating,
        label: t({
          id: "client-support-page.quality-rating",
          defaultMessage: "Quality Rating",
        }),
        startIcon: <HeartIcon />,
        endIcon:
          whatsappQualityRating?.rating === "RED" ? (
            <ExclamationMarkIcon />
          ) : undefined,
      },
    ],
    [whatsappAgent, t, whatsappBusinessStatus, whatsappQualityRating]
  );

  return (
    <TabsLayout
      selectedTab={selectedTab}
      rightContent={
        selectedTab === WhatsappAccountTab.taskTemplates ||
        selectedTab === WhatsappAccountTab.messageTemplates ? (
          <Button
            className="h-10"
            iconEnd={<AddIcon />}
            onClick={subscriptionRequiredAction(() =>
              transitionTo({
                name: "whatsappTemplateForm",
                templateCategory:
                  selectedTab === WhatsappAccountTab.taskTemplates
                    ? WhatsappTemplateCategory.tasks
                    : WhatsappTemplateCategory.message,
              })
            )}
          >
            {t({
              id: "client-support-page.create-template",
              defaultMessage: "Create template",
            })}
          </Button>
        ) : undefined
      }
      tabs={[
        {
          id: WhatsappAccountTab.account,
          title: t({
            id: "client-support-page.account",
            defaultMessage: "Account",
          }),
          onClick: () => handleSafeTabChange(WhatsappAccountTab.account),
        },
        {
          id: WhatsappAccountTab.taskTemplates,
          title: t({
            id: "client-support-page.task-templates",
            defaultMessage: "Task Templates",
          }),
          onClick: () => handleSafeTabChange(WhatsappAccountTab.taskTemplates),
          hidden: !isConnected || isCx,
        },
        {
          id: WhatsappAccountTab.messageTemplates,
          title: t({
            id: "client-support-page.message-templates",
            defaultMessage: "Message Templates",
          }),
          onClick: () =>
            handleSafeTabChange(WhatsappAccountTab.messageTemplates),
          hidden: !isConnected,
        },
        {
          id: WhatsappAccountTab.logsHistory,
          title: t({
            id: "client-support-page.logs-history",
            defaultMessage: "Logs History",
          }),
          onClick: () => handleSafeTabChange(WhatsappAccountTab.logsHistory),
          hidden: isCx,
        },
      ]}
    >
      <MultiSectionLayout
        classNames={{
          content:
            "flex flex-col gap-8 p-8 bg-grey-100 max-lg:gap-4 max-lg:p-4",
        }}
        leftSide={
          shouldShowSideMenu && (
            <SideMenuPanel className="w-[320px] max-lg:w-14">
              <SideMenuList>
                {sideMenuItems.map((item) => (
                  <SideMenuListItem
                    key={item.id}
                    label={item.label}
                    startIcon={item.startIcon}
                    endIcon={item.endIcon}
                    isSelected={selectedMenuTab === item.id}
                    onClick={() => handleSafeMenuTabChange(item.id)}
                  />
                ))}
              </SideMenuList>
            </SideMenuPanel>
          )
        }
      >
        {(() => {
          if (selectedTab === WhatsappAccountTab.account) {
            switch (selectedMenuTab) {
              case WhatsappAccountMenuTab.generalInfo:
                return (
                  <WhatsappGeneralInfoTab
                    setSelectedMenuTab={setSelectedMenuTab}
                  />
                );
              case WhatsappAccountMenuTab.businessVerification:
                return <WhatsappBusinessVerificationTab />;
              case WhatsappAccountMenuTab.twoFactorAuthentication:
                return <WhatsappAuthenticationTab />;
              case WhatsappAccountMenuTab.waProfile:
                return <WhatsappProfileTab />;
              case WhatsappAccountMenuTab.clientsContactDetails:
                return <WhatsappClientsContactDetailsTab />;
              case WhatsappAccountMenuTab.qualityRating:
                return <WhatsappQualityRatingTab />;
              default:
                return null;
            }
          }

          if (selectedTab === WhatsappAccountTab.taskTemplates) {
            return (
              <WhatsappTemplatesTab
                key={selectedTab}
                templateCategory={WhatsappTemplateCategory.tasks}
              />
            );
          }

          if (selectedTab === WhatsappAccountTab.messageTemplates) {
            return (
              <WhatsappTemplatesTab
                key={selectedTab}
                templateCategory={WhatsappTemplateCategory.message}
              />
            );
          }

          if (selectedTab === WhatsappAccountTab.logsHistory) {
            return <WhatsappLogsHistoryTab />;
          }

          return null;
        })()}
      </MultiSectionLayout>
    </TabsLayout>
  );
};
